import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, Chip, IconButton, Rating, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CURRENCY } from "../../constants/Variables";
import { roundTo2Decimal } from "../../utils";

const BasePackageSelectedCards = ({
    cardData,
    cardStyleProps,
    onDelete,
}) => {
    const { t } = useTranslation(["common"]);
    const { currentLanguage } = useSelector((state) => state.translationReducer);

    const isVariantItem = !!(cardData?.selectedVariant || cardData?.variantId);
    const effectivePrice = isVariantItem ? (cardData.selectedVariant?.finalPrice ?? cardData.finalPrice) : cardData.finalPrice;
    const effectiveDiscount = isVariantItem ? (cardData.selectedVariant?.discount ?? cardData.discount ?? 0) : (cardData.discount ?? 0);
    const availableQuantity = isVariantItem ? (cardData.selectedVariant?.quantity ?? cardData.quantity ?? 0) : cardData.quantity ?? 0;
    const finalPrice = effectivePrice ?? 0;

    const outOfStock = availableQuantity < cardData?.selectedQty;
    // const notAvailable = isVariantItem ? (cardData?.status === false || cardData.selectedVariant?.status === false) : (cardData.status === false);
    const notAvailable = isVariantItem
    ? (cardData?.status === false || cardData?.selectedVariant?.status === false)
    : (cardData?.status === false);
    
    const displayImage = isVariantItem && cardData.selectedVariant?.images?.length
        ? cardData.selectedVariant.images[0]
        : cardData?.coverImage;


    const variantLabel = isVariantItem && cardData.selectedVariant?.attributes
        ? Object.values(cardData.selectedVariant.attributes)
            .map((a) => <Chip key={a._id} label={a?.label?.[currentLanguage] || a?.label} size="small" variant="outlined" sx={{
                borderRadius: "6px",

                backgroundColor: a.value && /^#/.test(String(a.value)) ? a.value : "grey.100",
                color: a.value && /^#/.test(String(a.value)) ? "white" : "black",
            }} />)
        : null;

    const renderFlag = () => {
        if (outOfStock) {
            return <Chip label={t("outOfStock")} color="error" size="small" />;
        }
        if (notAvailable) {
            return <Chip label={t("notAvailable")} color="error" size="small" />;
        }
        return null;
    }

    return (
        <Box
            component="a"
            sx={{
                borderRadius: 2,
                textDecoration: "none",
                position: "relative",
                overflow: "visible",
                cursor: "pointer",
                display: "block",
                backgroundColor: "grey.100",
                ...cardStyleProps,
                "&:hover .card-button": {
                    opacity: 1,
                    visibility: "visible",
                    pointerEvents: "auto",
                },
            }}
        >
            {renderFlag()}
            <Box
                position="relative"
                overflow="visible"
                borderRadius={2}
                px={1}
                bgcolor="text.white"
            >
                {onDelete && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDelete(cardData);
                        }}
                        sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            zIndex: 1,
                            bgcolor: "background.paper",
                            boxShadow: 1,
                            "&:hover": { bgcolor: "grey.200" },
                            color: "error.main",
                        }}
                        aria-label={t("delete")}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                )}
                <Box
                    height={180}
                    borderRadius={2}
                    bgcolor="text.white"
                    overflow="hidden"
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box
                        component={"img"}
                        src={displayImage ?? cardData?.coverImage}
                        sx={{
                            height: "180px",
                            aspectRatio: 16 / 9,
                            width: "100%",
                            objectFit: "contain",
                            loading: "lazy",
                        }}
                    />
                </Box>

            </Box>

            <Box p={0.5}>
                <Rating
                    name="size-medium"
                    size="small"
                    value={cardData?.rating}
                    readOnly
                    sx={{ mb: 0.5 }}
                />
                <Typography
                    fontSize="1rem"
                    color={"text.white"}
                    sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {cardData.name[currentLanguage]}
                </Typography>
                {variantLabel && (
                    <Stack my={0.5} direction="row" gap={0.5} flexWrap="wrap">
                        {variantLabel}
                    </Stack>
                )}

                <Stack mt={0.5}>
                    <Typography
                        variant="body2"
                        color="error.main"
                        sx={{ textDecoration: "line-through" }}
                        visibility={effectiveDiscount > 0 ? "visible" : "hidden"}
                    >
                        {roundTo2Decimal(effectivePrice + effectiveDiscount)} {CURRENCY}
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" color="text.main">
                        {roundTo2Decimal(finalPrice)}  {CURRENCY}
                    </Typography>

                </Stack>

                <Stack mt={1}>
                    <Typography variant="body2" color="text.darkGrey">
                        {t("totalQuantity")}: {availableQuantity}
                    </Typography>

                    <Typography variant="body2" color="text.darkGrey">
                        {t("remainingQuantity")}: {availableQuantity - cardData?.selectedQty > 0 ? availableQuantity - cardData?.selectedQty : 0}
                    </Typography>
                    <Typography variant="body2" color="text.darkGrey">
                        {t("selectedQuantity")}: {cardData?.selectedQty}
                    </Typography>
                </Stack>
                <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                        {roundTo2Decimal(finalPrice)} {CURRENCY} × {cardData.selectedQty ?? 1}
                    </Typography>
                    <Stack direction="row" alignItems="baseline" gap={0.5}>
                        <Typography variant="caption" color="text.secondary">
                            {t("total")}:
                        </Typography>
                        <Typography fontSize="1.125rem" color="text.errorRed" fontWeight={600}>
                            {roundTo2Decimal((finalPrice ?? 0) * (cardData.selectedQty ?? 1))} {CURRENCY}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    )
}

export default BasePackageSelectedCards;